import { Contract } from 'ethers';
/**
 * Get multiple ERC-1155 token balances using native balanceOfBatch function.
 * This is the most efficient method - it's built into the ERC-1155 standard.
 *
 * @param contract - ERC-1155 contract instance
 * @param accountAddress - Account to check balances for
 * @param tokenIds - Array of tokenIds to check
 * @returns Array of BatchBalanceResult with balance for each tokenId
 *
 * @example
 * ```typescript
 * const results = await getERC1155BalanceOfBatch(
 *   contract,
 *   '0xabc...',
 *   ['1', '2', '3', '4', '5']
 * );
 * // 1 RPC call returns all balances
 * ```
 */
export async function getERC1155BalanceOfBatch(contract, accountAddress, tokenIds) {
    if (tokenIds.length === 0) {
        return [];
    }
    try {
        // ERC-1155 balanceOfBatch requires arrays of same length
        // accounts[i] corresponds to ids[i]
        const accounts = tokenIds.map(() => accountAddress);
        // Call native balanceOfBatch - 1 RPC call for ALL tokenIds
        const balances = await contract.balanceOfBatch(accounts, tokenIds);
        return tokenIds.map((tokenId, index) => ({
            tokenId,
            balance: balances[index],
            success: true
        }));
    }
    catch (error) {
        console.warn('[ERC1155] balanceOfBatch failed:', error);
        return tokenIds.map(tokenId => ({
            tokenId,
            balance: 0n,
            success: false,
            error: `balanceOfBatch failed: ${error}`
        }));
    }
}
/**
 * Get multiple token balances for ERC-1155 contracts.
 * Uses the native balanceOfBatch function (1 RPC call for ALL tokens).
 * Falls back to individual calls only if balanceOfBatch fails.
 *
 * @param provider - Ethers provider
 * @param contractAddress - Token contract address
 * @param contractInterface - Contract interface (must include balanceOfBatch for ERC-1155)
 * @param accountAddress - Account to check balances for
 * @param tokenIds - Array of tokenIds to check
 * @param isERC1155 - Whether the contract is ERC-1155 (default: true)
 * @returns Array of BatchBalanceResult
 */
export async function getBatchBalancesWithFallback(provider, contractAddress, contractInterface, accountAddress, tokenIds, isERC1155 = true) {
    if (tokenIds.length === 0)
        return [];
    const contract = new Contract(contractAddress, contractInterface, provider);
    // For single token, direct call is more efficient
    if (tokenIds.length === 1) {
        try {
            const balance = isERC1155
                ? await contract.balanceOf(accountAddress, tokenIds[0])
                : await contract.balanceOf(accountAddress);
            return [{
                    tokenId: tokenIds[0],
                    balance: balance,
                    success: true
                }];
        }
        catch (error) {
            return [{
                    tokenId: tokenIds[0],
                    balance: 0n,
                    success: false,
                    error: `${error}`
                }];
        }
    }
    // For ERC-1155: Use native balanceOfBatch (1 RPC call for ALL tokens)
    if (isERC1155) {
        const results = await getERC1155BalanceOfBatch(contract, accountAddress, tokenIds);
        // If native call succeeded, return results
        if (results.some(r => r.success)) {
            return results;
        }
        // Otherwise fall through to individual calls
    }
    // Fallback: individual calls in parallel (for non-ERC1155 or if balanceOfBatch failed)
    const promises = tokenIds.map(async (tokenId) => {
        try {
            const balance = isERC1155
                ? await contract.balanceOf(accountAddress, tokenId)
                : await contract.balanceOf(accountAddress);
            return {
                tokenId,
                balance: balance,
                success: true
            };
        }
        catch (error) {
            return {
                tokenId,
                balance: 0n,
                success: false,
                error: `${error}`
            };
        }
    });
    return Promise.all(promises);
}
